import yaml from 'js-yaml';

// Minimal frontmatter parser for browser (no Buffer)
const parseFrontmatter = (text) => {
  const lines = text.split('\n');
  if (lines[0]?.trim() !== '---') {
    return { data: {}, content: text };
  }
  let end = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '---') {
      end = i;
      break;
    }
  }
  if (end === -1) {
    return { data: {}, content: text };
  }
  const yamlText = lines.slice(1, end).join('\n');
  const content = lines.slice(end + 1).join('\n');
  let data = {};
  try {
    data = yaml.load(yamlText) || {};
  } catch (e) {
    console.warn('Failed to parse frontmatter YAML', e);
  }
  return { data, content };
};

// Function to load individual work data
const PUBLIC_BASE = process.env.PUBLIC_URL || '';

const fetchJson = async (path) => {
  const response = await fetch(`${PUBLIC_BASE}${path}`);
  if (!response.ok) throw new Error(`Failed to fetch ${path}: ${response.status}`);
  return response.json();
};

export const getWorkProjects = async () => {
  return fetchJson('/data/work-projects.json');
};

export const getAiProjects = async () => {
  return fetchJson('/data/ai-projects.json');
};

export const getCaseStudies = async () => {
  return fetchJson('/data/case-studies.json');
};

const loadItemFromPublic = async (options) => {
  const { indexLoader, folder, id } = options;
  const items = await indexLoader();
  const item = items.find((x) => x.id === id);
  if (!item) return null;

  if (item.dataFile && item.dataFile.endsWith('.md')) {
    const url = `${PUBLIC_BASE}/data/${folder}/${item.dataFile}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch Markdown at ${url}: ${response.status}`);
    const fileText = await response.text();
    const parsed = parseFrontmatter(fileText);
    return {
      ...parsed.data,
      id: item.id,
      title: item.title,
      description: item.description,
      heroImage: item.image,
      image: item.image,
      body: parsed.content
    };
  }

  return {
    id: item.id,
    title: item.title,
    description: item.description,
    heroImage: item.image,
    image: item.image,
    body: ''
  };
};

export const loadAiProjectData = async (id) => {
  return loadItemFromPublic({ indexLoader: getAiProjects, folder: 'ai-projects', id });
};

export const loadCaseStudyData = async (id) => {
  return loadItemFromPublic({ indexLoader: getCaseStudies, folder: 'case-studies', id });
};

export const loadWorkData = async (workId) => {
  try {
    const workIndex = await getWorkProjects();
    let workItem = workIndex.find(item => item.id === workId);
    if (!workItem) {
      // Fallback: try fetching a markdown file named after workId directly
      const fallbackMd = `/data/work/${workId}.md`;
      try {
        const response = await fetch(fallbackMd);
        if (response.ok) {
          const fileText = await response.text();
          const parsed = parseFrontmatter(fileText);
          return { ...parsed.data, body: parsed.content };
        }
      } catch (_) {
        // ignore; will return null below
      }
      return null;
    }

    // If data file is Markdown, fetch from public and parse frontmatter
    if (workItem.dataFile && workItem.dataFile.endsWith('.md')) {
      const url = `${PUBLIC_BASE}/data/work/${workItem.dataFile}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch Markdown at ${url}: ${response.status}`);
      const fileText = await response.text();
      const parsed = parseFrontmatter(fileText);
      return {
        ...parsed.data,
        id: workItem.id,
        title: workItem.title,
        description: workItem.description,
        heroImage: workItem.image,
        image: workItem.image,
        body: parsed.content
      };
    }

    // Legacy JSON import support (kept for compatibility if needed)
    try {
      const workData = await import(`../data/work/${workItem.dataFile}`);
      return workData.default;
    } catch (_) {
      return {
        id: workItem.id,
        title: workItem.title,
        description: workItem.description,
        heroImage: workItem.image,
        image: workItem.image
      };
    }
  } catch (error) {
    console.error(`Error loading work data for ${workId}:`, error);
    return null;
  }
};

// Function to get navigation info for a work item
export const getWorkNavigation = async (currentWorkId) => {
  const workIndex = await getWorkProjects();
  const currentIndex = workIndex.findIndex(item => item.id === currentWorkId);
  if (currentIndex === -1) {
    return { prev: null, next: null, current: null };
  }
  const prevItem = currentIndex > 0 ? workIndex[currentIndex - 1] : null;
  const nextItem = currentIndex < workIndex.length - 1 ? workIndex[currentIndex + 1] : null;
  const currentItem = workIndex[currentIndex];
  return {
    prev: prevItem,
    next: nextItem,
    current: currentItem,
    currentIndex: currentIndex + 1,
    total: workIndex.length
  };
};

export const getAiNavigation = async (currentId) => {
  const index = await getAiProjects();
  const currentIndex = index.findIndex(item => item.id === currentId);
  if (currentIndex === -1) return { prev: null, next: null, current: null };
  const prevItem = currentIndex > 0 ? index[currentIndex - 1] : null;
  const nextItem = currentIndex < index.length - 1 ? index[currentIndex + 1] : null;
  const currentItem = index[currentIndex];
  return { prev: prevItem, next: nextItem, current: currentItem, currentIndex: currentIndex + 1, total: index.length };
};

export const getCaseStudiesNavigation = async (currentId) => {
  const index = await getCaseStudies();
  const currentIndex = index.findIndex(item => item.id === currentId);
  if (currentIndex === -1) return { prev: null, next: null, current: null };
  const prevItem = currentIndex > 0 ? index[currentIndex - 1] : null;
  const nextItem = currentIndex < index.length - 1 ? index[currentIndex + 1] : null;
  const currentItem = index[currentIndex];
  return { prev: prevItem, next: nextItem, current: currentItem, currentIndex: currentIndex + 1, total: index.length };
};

// Function to get all work items (for WorkLanding)
// Deprecated: use getAiProjects/getWorkProjects/getCaseStudies instead
export const getAllWorkItems = async () => {
  const [ai, work, cs] = await Promise.all([
    getAiProjects().catch(() => []),
    getWorkProjects().catch(() => []),
    getCaseStudies().catch(() => [])
  ]);
  return [...ai, ...work, ...cs];
};